import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, Checkbox, FormControlLabel, Stack, TextField, Typography } from '@mui/material';
import { adminFetch } from '../services/adminFetch';

const endpoint = '/api/admin/draws/104/correction';
const storageKey = 'fliplottery.draw104.correction.operation';
const restorationOperationId = 'd96c4569-9fbf-4094-aeaa-cef62dda6428';
const excludedTicketIds = [17478, 17565, 17582, 18269, 18453, 18789, 19121, 19163, 19505, 20098];
const uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const auditId = value => (Number.isSafeInteger(value) && value > 0)
  || (typeof value === 'string' && /^[1-9][0-9]*$/.test(value));
const money = value => typeof value === 'string' && /^(0|[1-9][0-9]*)\.[0-9]{2}$/.test(value);

function valid(plan) {
  if (!plan || plan.draw_id !== 104 || Number(plan.restore_audit_id) !== 41255
    || Number(plan.configuration_audit_id) !== 41333) return false;
  if (plan.state === 'not_prepared') {
    return plan.restore_count === 2800 && plan.restoration_operation_id === restorationOperationId;
  }
  if (!uuid.test(plan.operation_id || '') || !auditId(plan.preparation_audit_id)) return false;
  if (plan.state === 'restored') {
    return Number.isInteger(plan.eligible_count) && plan.eligible_count >= 0
      && Number.isInteger(plan.held_count) && plan.held_count >= 0
      && plan.eligible_count + plan.held_count === 2800
      && typeof plan.plan_hash === 'string' && plan.plan_hash.length > 0 && plan.winner_count === 60
      && money(plan.gross_total) && money(plan.net_total)
      && ['amount', 'deduction_amount', 'net_amount'].every(field => money(plan.prize?.[field]))
      && money(plan.prize?.deduction_percent) && Number(plan.prize.deduction_percent) <= 100
      && Array.isArray(plan.excluded_ticket_ids) && plan.excluded_ticket_ids.length === 10
      && new Set(plan.excluded_ticket_ids.map(Number)).size === 10
      && plan.excluded_ticket_ids.every(id => excludedTicketIds.includes(Number(id)));
  }
  return ['completed', 'voided'].includes(plan.state) && (plan.state !== 'voided' || auditId(plan.void_audit_id)) && auditId(plan.replacement_draw_id)
    && auditId(plan.execute_audit_id) && Array.isArray(plan.winners) && plan.winners.length === 60
    && plan.winners.every(winner => auditId(winner?.id) && auditId(winner?.ticket_id)
      && !excludedTicketIds.includes(Number(winner.ticket_id)))
    && new Set(plan.winners.map(winner => String(winner.id))).size === 60
    && new Set(plan.winners.map(winner => String(winner.ticket_id))).size === 60;
}

function responseMessage(response, fallback) {
  return response?.status === false && typeof response.message === 'string'
    ? response.message : fallback;
}

export default function Draw104Correction() {
  const [plan, setPlan] = useState(null), [error, setError] = useState(''), [busy, setBusy] = useState(false);
  const [password, setPassword] = useState(''), [confirmed, setConfirmed] = useState(false);
  const lock = useRef(false), operation = useRef(null);

  const load = useCallback(async () => {
    const response = await adminFetch(endpoint, { method: 'GET', cache: 'no-store' });
    if (response?.status !== true || !valid(response.correction)) {
      throw new Error(responseMessage(response, 'Cannot verify correction status. Reload before any submission.'));
    }
    if (response.correction.operation_id) {
      // The committed server operation is authoritative after a lost response or browser restart.
      operation.current = response.correction.operation_id;
      localStorage.setItem(storageKey, operation.current);
    }
    setPlan(response.correction);
    return response.correction;
  }, []);

  const refresh = useCallback(async () => {
    if (lock.current) return;
    lock.current = true; setBusy(true); setError(''); setPlan(null);
    setPassword(''); setConfirmed(false);
    try { await load(); }
    catch (_error) { setError('Cannot verify correction status. Check your access and connection, then reload before any submission.'); }
    finally { lock.current = false; setBusy(false); }
  }, [load]);
  useEffect(() => { refresh(); }, [refresh]);

  async function submit(action) {
    if (lock.current || !plan || !confirmed || !password) return;
    if (action === 'execute' && (plan.state !== 'restored' || plan.eligible_count < 60)) return;
    if (action === 'prepare' && plan.state !== 'not_prepared') return;
    lock.current = true; setBusy(true); setError('');
    try {
      if (action === 'prepare' && (!operation.current || operation.current === restorationOperationId)) {
        const saved = localStorage.getItem(storageKey)?.toLowerCase();
        // The database restoration is already committed and must never identify this new preparation.
        operation.current = uuid.test(saved || '') && saved !== restorationOperationId ? saved : crypto.randomUUID();
      }
      if (!uuid.test(operation.current || '')) throw new Error('No verified operation ID');
      // Persist before sending; if storage fails, do not send an unrecoverable request.
      localStorage.setItem(storageKey, operation.current);
    } catch (_error) {
      setError('Unable to retain a safe retry in this browser. Enable site storage and reload correction status.');
      setPassword(''); setConfirmed(false); lock.current = false; setBusy(false);
      return;
    }

    const requestedOperation = operation.current;
    let response;
    try {
      try {
        response = await adminFetch(`${endpoint}/${action}`, {
          method: 'POST',
          body: JSON.stringify({ operation_id: requestedOperation, password, confirmed: true,
            ...(action === 'execute' ? { plan_hash: plan.plan_hash } : {}) })
        });
      } catch (_error) {
        // A rejected request can still have committed. Always reconcile before another submission.
      }
      setPlan(null);
      const actual = await load();
      const achieved = action === 'prepare' ? actual.state !== 'not_prepared' : actual.state === 'completed';
      if (actual.operation_id && actual.operation_id !== requestedOperation) {
        setError('A different correction operation was recorded. Its current state has been recovered; review it before continuing.');
      } else if (!achieved) {
        setError(responseMessage(response, 'The operation is not confirmed. Review the recovered state before retrying with the same operation ID.'));
      }
    } catch (_error) {
      setPlan(null);
      setError('Outcome unverified. Reload correction status before retrying. No further operation is available until its state is verified.');
    } finally {
      setPassword(''); setConfirmed(false); lock.current = false; setBusy(false);
    }
  }

  return <Box sx={{ p: 3, maxWidth: 850, width: '100%' }}><Stack spacing={2}>
    <Typography variant="h4">Draw 104 correction</Typography>
    <Alert severity="warning">Restricted production operation. Ogenenyure, CEO, supplied the eligibility attestation for 2,800 tickets; a backup has not independently verified it. Preserve the original ten winners and their payments.</Alert>
    {error && <Alert severity="error" role="alert">{error}</Alert>}
    <Button onClick={refresh} disabled={busy}>Reload correction status</Button>
    {busy && <Typography role="status">Verifying operation…</Typography>}
    {Array.isArray(plan?.original_winnings) && <Typography>Original winnings: {plan.original_winnings.filter(w => w.status === 'paid').length} paid; {plan.original_winnings.filter(w => w.status !== 'paid').length} not marked paid. Settlement continues independently.</Typography>}
    {plan?.state === 'not_prepared' && <>
      <Typography>2,800 tickets were already restored under audit {plan.restore_audit_id}. The configured winner count was saved as 60 under audit {plan.configuration_audit_id}.</Typography>
      <Typography>Prepare the replacement participation mapping using those existing records. Preparation does not restore tickets again, change the original draw, select winners or make payments.</Typography>
    </>}
    {plan?.state === 'restored' && <>
      <Typography>Replacement participation prepared. Preparation audit {plan.preparation_audit_id}; original restoration audit {plan.restore_audit_id}; configuration audit {plan.configuration_audit_id}. Eligible tickets: {plan.eligible_count}; held: {plan.held_count}.</Typography>
      <Typography>Replacement: 60 distinct tickets. Additional gross prizes: {plan.gross_total}; net prizes: {plan.net_total}. Original winnings remain recorded separately.</Typography>
      <Typography>Per winning ticket: gross {plan.prize.amount}; withholding {plan.prize.deduction_percent}% ({plan.prize.deduction_amount}); net {plan.prize.net_amount}. All ten original winning tickets are excluded.</Typography>
      <Typography sx={{ overflowWrap: 'anywhere' }}>Plan hash: {plan.plan_hash}</Typography>
      <Alert severity="warning">Executing records results immediately. Settlement remains disabled; execution does not authorize a payment. It does not send winner notifications or run the live animation. Execute only after release and settlement safeguards have been verified.</Alert>
    </>}
    {plan?.state === 'completed' && Number(plan.replacement_draw_id) !== 108 && <Alert severity="success">Verified replacement draw {plan.replacement_draw_id}: {plan.winners.length} recorded winners. Audit {plan.execute_audit_id}. No further execution is available.</Alert>}
    {plan && !['completed','voided'].includes(plan.state) && <>
      <TextField type="password" label="Your Super Admin password" value={password} onChange={event => setPassword(event.target.value)} autoComplete="current-password" disabled={busy} />
      <FormControlLabel control={<Checkbox checked={confirmed} onChange={event => setConfirmed(event.target.checked)} disabled={busy} />}
        label={plan.state === 'restored' ? 'I confirm the displayed replacement prize liability and execution' : 'I confirm preparation from the existing restoration and configuration audits; do not restore tickets again'} />
      <Button variant="contained" color="warning" disabled={busy || !password || !confirmed || (plan.state === 'restored' && plan.eligible_count < 60)} onClick={() => submit(plan.state === 'restored' ? 'execute' : 'prepare')}>
        {plan.state === 'restored' ? 'Execute 60-winner replacement' : 'Prepare replacement participation'}
      </Button>
    </>}
    {plan && Number(plan.replacement_draw_id) === 108 && <Draw108Recovery plan={plan} reload={load} />}
    <Button href="/admin/draws">Return to Draws</Button>
  </Stack></Box>;
}

function Draw108Recovery({ plan, reload }) {
  const [password,setPassword]=useState(''),[confirmed,setConfirmed]=useState(false);
  const [busy,setBusy]=useState(false),[error,setError]=useState('');
  const lock=useRef(false), settlement=plan.settlement;
  const ready=settlement?.state==='awaiting_authorization' && settlement.credited_count===60
    && settlement.lost_count===2740 && settlement.winner_count===70
    && money(settlement.net_total) && /^[a-f0-9]{64}$/.test(settlement.plan_hash||'');
  const finalized=settlement?.state==='finalized' && auditId(settlement.audit_id)
    && settlement.credited_count===60 && settlement.lost_count===2740 && settlement.winner_count===70;
  async function finalize(){
    if(lock.current||!ready||!password||!confirmed)return;
    lock.current=true;setBusy(true);setError('');
    try{
      const key='fliplottery.draw108.finalization.operation';
      let id=localStorage.getItem(key);if(!uuid.test(id||'')){id=crypto.randomUUID();localStorage.setItem(key,id);}
      let response;
      try{response=await adminFetch(`${endpoint}/finalize`,{method:'POST',body:JSON.stringify({operation_id:id,password,confirmed:true,plan_hash:settlement.plan_hash})});}catch(_){/* Lost responses require reconciliation, never another payment intent. */}
      const actual=await reload();
      if(actual.settlement?.state!=='finalized'||!auditId(actual.settlement.audit_id))setError(responseMessage(response,'Settlement not confirmed. Review the recovered state before retrying with the same operation ID.'));
      lock.current=false;
    }catch(_){setError('Outcome unverified. Reload correction status before retrying. No further submission is available until verified.');}
    finally{setPassword('');setConfirmed(false);setBusy(false);}
  }
  return <Stack spacing={2}>
    <Alert severity="info">Final decision: retain all 70 winners. No cancellation, new selection, or automatic settlement is authorized.</Alert>
    {error&&<Alert severity="error">{error}</Alert>}
    {finalized ? <Alert severity="success">Finalization verified: 60 additional wallet credits totaling ₦{settlement.net_total}; 70 winning tickets retained; 2,740 non-winning tickets lost. Audit {settlement.audit_id}. Original ten payouts unchanged.</Alert> : ready ? <>
      <Typography>Review: 60 held winnings; gross ₦{settlement.gross_total}; net wallet credits ₦{settlement.net_total}. Preserve the original ten paid winnings. Mark exactly 2,740 non-winning tickets lost. Both draws remain completed.</Typography>
      <TextField label="Settlement Super Admin password" type="password" value={password} onChange={e=>setPassword(e.target.value)} disabled={busy||lock.current} autoComplete="current-password" />
      <FormControlLabel control={<Checkbox checked={confirmed} onChange={e=>setConfirmed(e.target.checked)} disabled={busy||lock.current}/>} label="I authorize the displayed 60 wallet credits and 2,740 lost tickets, retaining all 70 winners" />
      <Button variant="contained" color="warning" disabled={busy||lock.current||!password||!confirmed} onClick={finalize}>Settle 60 winners and finalize tickets</Button>
    </> : <Alert severity="error">No verified settlement plan is available. Reload status; do not cancel or execute another draw.</Alert>}
  </Stack>;
}
